import { 
  summariesSignal, 
  activeSummaryTabIndexSignal,
  pendingNewMessagesCountSignal,
  daysSinceLastSummarySignal,
  isStaleSummarySignal 
} from '../../state/ai.store';
import { activeContactSignal, activeSummaryJobSignal, isLoadingSummariesSignal } from '../../state/contacts.store';
import { sseConnectionStatusSignal } from '../../services/sse';
import { ProjectsTab } from './ProjectsTab';
import { showToast } from '../../state/ui.store';
import { Loader2, Copy, Sparkles, Calendar, BookOpen, RefreshCw, Zap, Clock, Hash, FileText } from 'lucide-preact';
import { renderMarkdownToHtml, processInlineMarkdown } from '../../utils/markdownUtils';
import { apiFetch } from '../../services/api';

export function SummariesTab() {
  const summaries = summariesSignal.value;
  const activeContact = activeContactSignal.value;
  const activeIndex = activeSummaryTabIndexSignal.value;
  const summaryJob = activeSummaryJobSignal.value;
  const isLoadingSummaries = isLoadingSummariesSignal.value;
  const sseStatus = sseConnectionStatusSignal.value;

  const pendingNewMessages = pendingNewMessagesCountSignal.value;
  const daysSinceLastSummary = daysSinceLastSummarySignal.value;
  const isStaleSummary = isStaleSummarySignal.value;

  if (!activeContact) return null;

  if (isLoadingSummaries) {
    return (
      <div id="tab-summaries" class="tab-content flex-1 overflow-y-auto p-6 space-y-6 animate-pulse">
        <div class="h-4 w-44 bg-slate-800 rounded"></div>
        <div class="p-5 bg-slate-900/90 border border-slate-800 rounded-2xl space-y-4">
          <div class="h-5 w-1/2 bg-slate-800 rounded"></div>
          <div class="h-4 w-full bg-slate-800 rounded"></div>
          <div class="h-4 w-5/6 bg-slate-800 rounded"></div>
          <div class="h-4 w-2/3 bg-slate-800 rounded"></div>
        </div>
      </div>
    );
  }

  const currentSummary = summaries.length > 0 && activeIndex < summaries.length ? summaries[activeIndex] : null;
  const isJobActive = Boolean(summaryJob && summaryJob.status !== 'COMPLETED' && summaryJob.status !== 'FAILED');
  const pct = Math.min(Math.max(summaryJob?.percent || 0, 5), 100);

  let statusLabel = 'Processando síntese com IA...';
  if (summaryJob?.status === 'TRANSCRIBING') {
    statusLabel = (summaryJob.totalMedia && summaryJob.totalMedia > 0)
      ? `Transcrevendo mídias de áudio/imagem (${summaryJob.processedMedia || 0}/${summaryJob.totalMedia})...`
      : 'Transcrevendo mídias em segundo plano...';
  } else if (summaryJob?.status === 'EXTRACTING_ACTIONS' || summaryJob?.stage === 'EXTRACTING_ACTIONS') {
    statusLabel = 'Extraindo e organizando tarefas pendentes (85%)...';
  } else if (summaryJob?.status === 'GENERATING_SUMMARY') {
    statusLabel = 'Sintetizando e consolidando resumo executivo (60%)...';
  }

  const handleCopySummary = (summary: any) => {
    const textToCopy = `# ${summary.title}\n\n${summary.content}`;
    navigator.clipboard.writeText(textToCopy);
    showToast('Relatório Markdown copiado para a área de transferência!', 'success');
  };

  const handleFetchUpdates = async () => {
    if (!activeContact) return;
    try {
      showToast('Buscando novas mensagens para consolidar atualização...', 'info');
      const res = await apiFetch<any>(`/ai/summarize-update/${activeContact.phone_number}`, {
        method: 'POST'
      });

      if (res?.hasUpdates === false) {
        showToast(res.message || 'Sua síntese já está atualizada com as mensagens mais recentes.', 'info');
      } else if (res?.job) {
        activeSummaryJobSignal.value = res.job;
        showToast(`Job de atualização iniciado para ${res.newMessagesCount || 'novas'} mensagens!`, 'success');
      }
    } catch (err: any) {
      showToast(`Erro ao buscar atualizações: ${err.message}`, 'error');
    }
  };

  return (
    <div id="tab-summaries" class="tab-content flex-1 overflow-y-auto p-6 space-y-6">
      {isJobActive && (
        <div class="p-4 bg-slate-900/90 border border-amber-500/50 rounded-2xl space-y-2.5 shadow-xl animate-fade-in">
          <div class="flex items-center justify-between text-xs font-bold text-amber-300 gap-2">
            <span class="flex items-center gap-2 min-w-0">
              <Loader2 class="w-4 h-4 text-amber-400 animate-spin shrink-0" />
              <span class="truncate">{statusLabel}</span>
            </span>
            <span class="font-mono text-amber-300 bg-slate-950/80 px-2 py-0.5 rounded border border-amber-500/40 shrink-0">
              {pct}%
            </span>
          </div>
          <div class="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden border border-amber-500/30">
            <div
              class="bg-gradient-to-r from-amber-500 via-cyan-500 to-indigo-500 h-full transition-[width] duration-300 rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
          {sseStatus === 'disconnected' && (
            <p class="text-[10px] text-amber-400/90 font-mono flex items-center gap-1">
              <Zap class="w-3 h-3 text-amber-400 shrink-0" />
              <span>Conexão em tempo real oscilando — Watchdog de polling ativo.</span>
            </p>
          )}
        </div>
      )}

      {/* BANNER ALERTA DE RESUMO DESATUALIZADO (> 7 dias ou > 50 mensagens novas) */}
      {isStaleSummary && !isJobActive && (
        <div class="p-4 rounded-2xl bg-gradient-to-r from-amber-950/70 via-slate-900 to-cyan-950/70 border border-amber-500/60 shadow-2xl space-y-3 animate-pulse-border">
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3">
              <div class="p-2 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-400/30 shrink-0 mt-0.5">
                <Zap class="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 class="text-xs font-extrabold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
                  Síntese Executiva Desatualizada
                </h4>
                <p class="text-xs text-amber-100/90 leading-relaxed font-medium mt-0.5">
                  Existem <strong class="text-amber-300 font-extrabold">{pendingNewMessages} novas mensagens</strong>
                  {daysSinceLastSummary > 0 ? ` (${daysSinceLastSummary} dias de conversas)` : ''} acumuladas desde a última geração.
                </p>
              </div>
            </div>
            <button
              onClick={handleFetchUpdates}
              class="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-cyan-500 hover:from-amber-400 hover:to-cyan-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition transform hover:scale-105 shrink-0 flex items-center gap-1.5 cursor-pointer"
            >
              <RefreshCw class="w-3.5 h-3.5" />
              <span>Atualizar Agora</span>
            </button>
          </div>
        </div>
      )}

      <div class="space-y-3">
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Sparkles class="w-3.5 h-3.5 text-cyan-400" /> Relatório & Síntese Executiva
          </h3>

          <button
            onClick={handleFetchUpdates}
            disabled={isJobActive}
            class="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-300 hover:text-cyan-200 text-xs font-bold rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50"
            title="Buscar novas mensagens e consolidar atualização executiva"
          >
            <RefreshCw class={`w-3.5 h-3.5 ${isJobActive ? 'animate-spin' : ''}`} />
            <span>Buscar Atualizações</span>
          </button>
        </div>

        {summaries.length === 0 && !isJobActive ? (
          <div class="p-8 text-center text-xs text-slate-400 font-medium bg-slate-900/60 rounded-2xl border border-slate-800 space-y-2">
            <BookOpen class="w-8 h-8 text-slate-600 mx-auto mb-1" />
            <p class="font-bold text-slate-300">Nenhum resumo executivo gerado até o momento para {activeContact.name}.</p>
            <p>Clique em <strong class="text-cyan-400">Buscar Atualizações</strong> ou Gerar Síntese no topo para criar o primeiro relatório.</p>
          </div>
        ) : (
          <div class="space-y-4">
            {/* Sub-tabs for version lineage (Relatório Principal + Atualizações) */}
            <div class="flex items-center gap-2 border-b border-slate-800 pb-2 overflow-x-auto">
              {summaries.map((s, idx) => {
                const isUpdate = s.version_type === 'update';
                const versionNum = s.version_number || 1;
                const IconComponent = isUpdate ? Zap : FileText;
                const labelText = isUpdate ? `Atualização #${versionNum}` : `Relatório Consolidado${versionNum > 1 ? ` (v#${versionNum})` : ''}`;

                return (
                  <button
                    key={s.id || idx}
                    onClick={() => activeSummaryTabIndexSignal.value = idx}
                    class={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5 shrink-0 ${
                      activeIndex === idx
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-sm'
                        : 'text-slate-400 hover:text-slate-200 bg-slate-900/60 border border-slate-800'
                    }`}
                  >
                    <IconComponent class="w-3.5 h-3.5 text-cyan-400" />
                    <span>{labelText}</span>
                  </button>
                );
              })}
            </div>

            {currentSummary && (
              <div class="p-5 bg-slate-900/90 border border-cyan-500/30 rounded-2xl space-y-4 shadow-xl">
                <div class="flex items-start justify-between gap-4 border-b border-slate-800 pb-3">
                  <div>
                    <div class="flex flex-wrap items-center gap-2 mb-1">
                      <span class="text-[10px] font-mono font-extrabold px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 flex items-center gap-1">
                        {currentSummary.version_type === 'update' ? (
                          <>
                            <Zap class="w-3 h-3 text-cyan-400" />
                            <span>Atualização Sucinta (Bate-Pronto #{currentSummary.version_number || 1})</span>
                          </>
                        ) : (
                          <>
                            <FileText class="w-3 h-3 text-cyan-400" />
                            <span>Relatório Consolidado (v#{currentSummary.version_number || 1})</span>
                          </>
                        )}
                      </span>
                      <span class="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                        <Clock class="w-3 h-3 text-slate-500" />
                        {new Date(currentSummary.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>

                    <h4 class="text-base font-extrabold text-slate-50 tracking-tight leading-snug">{currentSummary.title}</h4>

                    {/* SUTIL EXIBIÇÃO DO ALCANCE DE MENSAGENS NO BANCO */}
                    <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400/90 font-medium">
                      {(currentSummary.first_message_timestamp || currentSummary.last_message_timestamp) && (
                        <span class="flex items-center gap-1">
                          <Calendar class="w-3 h-3 text-cyan-400/80" />
                          <span>
                            Abrange de {currentSummary.first_message_timestamp ? new Date(currentSummary.first_message_timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'início'}
                            {' a '}
                            {currentSummary.last_message_timestamp ? new Date(currentSummary.last_message_timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'agora'}
                          </span>
                        </span>
                      )}
                      {(currentSummary.first_message_id || currentSummary.last_message_id) && (
                        <span class="flex items-center gap-1 text-slate-400 font-mono text-[10px]">
                          <Hash class="w-3 h-3 text-slate-500" />
                          <span>
                            msgs #{currentSummary.first_message_id ? String(currentSummary.first_message_id).slice(-6) : '---'}
                            {' → '}
                            #{currentSummary.last_message_id ? String(currentSummary.last_message_id).slice(-6) : '---'}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleCopySummary(currentSummary)}
                    class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white text-xs font-bold rounded-xl border border-slate-700 transition flex items-center gap-1.5 cursor-pointer shrink-0 shadow-sm"
                    title="Copiar relatório Markdown"
                  >
                    <Copy class="w-3.5 h-3.5" />
                    <span>Copiar Markdown</span>
                  </button>
                </div>

                <div 
                  class="markdown-rendered text-xs leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(currentSummary.content) }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div class="space-y-3 pt-4 border-t border-slate-800">
        <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">Tópicos e Projetos Identificados</h3>
        <ProjectsTab />
      </div>
    </div>
  );
}
